import * as AmplifyAuth from "aws-amplify/auth";
import { ROLES } from "constants/contants";
import { encryptWithBase64DerPublicKey, urlToBase64 } from "constants/utils";
import useUserKeypairs from "hooks/useUserKeypairs";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  injectLogout,
  POST_AUTH_LOGIN,
  POST_AUTH_LOGIN_SESSION_KEY,
  POST_AUTH_LOGOUT,
  VERIFY_OTP,
} from "services/ApiServices";
import {
  clearUser,
  setSingleUser,
  useGetUserFromRedux,
  UserInterface,
} from "slices/users";

interface LoginCredentials {
  email: string;
  password: string;
}

interface OtpVerification {
  enteredOtp: string;
}

interface LoginResponse {
  data: any;
  status: "idle" | "pending" | "resolved" | "rejected";
}

interface AuthContextType {
  setUser: (user: Partial<UserInterface>) => void;
  user: UserInterface | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: (type: string) => Promise<void>;
  loginResponse: LoginResponse;
  verifyOtp: (otp: OtpVerification) => Promise<void>;
  logout_reserPasswordAfter60Day: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

const logOutAmplifyIfLoggedIn = async () => {
  try {
    const session = await AmplifyAuth.fetchAuthSession();
    await AmplifyAuth.signOut();
  } catch (err) {
    console.error("Error during Amplify logout:", err);
  }
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const user = useGetUserFromRedux() as UserInterface | null;
  const dispatch = useDispatch();
  const [loginResponse, setLoginResponse] = React.useState<LoginResponse>({
    data: null,
    status: "idle",
  });
  const [verifyOtpResponse, setVerifyOtpResponse] =
    React.useState<LoginResponse>({
      data: null,
      status: "idle",
    });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userKeypairs = useUserKeypairs();

  const login = async ({ email, password }: LoginCredentials) => {
    let toastId: React.ReactText | undefined;
    try {
      if (loginResponse.status === "pending") {
        return;
      }
      setLoginResponse({
        data: null,
        status: "pending",
      });
      await logOutAmplifyIfLoggedIn();
      toastId = toast.loading(t("pleaseWait"));
      const sessionKey = await POST_AUTH_LOGIN_SESSION_KEY();
      const { data: resData } = sessionKey.data;

      const encData = encryptWithBase64DerPublicKey(
        JSON.stringify({
          email,
          password,
        }),
        resData.publicKey
      );

      const response = await POST_AUTH_LOGIN({
        session_id: resData.sessionId,
        encrypted_payload: encData,
      });
      const result = await AmplifyAuth.signIn({
        username: email,
        password,
      });
      const { data } = response.data;

      if (
        result.nextStep.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        handleUpdateUser({
          emailVerified: data.emailVerified,
        });
        navigate("reset-password", {
          state: {
            signInStep: "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED",
          },
        });
      } else if (data.is2FA) {
        navigate("two-factor-auth");
      } else {
        await onAuthenticationResponse(data);
      }
      setLoginResponse({
        data: response.data,
        status: "resolved",
      });
    } catch (err: unknown) {
      setLoginResponse({
        data: null,
        status: "rejected",
      });
      const error = err as Error & {
        response?: { data?: { message?: string } };
      };
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          t("toast.FailedLogin")
      );
    } finally {
      if (toastId) toast.dismiss(toastId);
    }
  };

  const handleUpdateUser = (user: Partial<UserInterface>) => {
    dispatch(setSingleUser(user));
  };

  const verifyOtp = async ({ enteredOtp }: OtpVerification) => {
    let toastId: React.ReactText | undefined;
    try {
      if (verifyOtpResponse.status === "pending") {
        return;
      }
      toastId = toast.loading(t("pleaseWait"));
      setVerifyOtpResponse({
        data: null,
        status: "pending",
      });
      const sessionKey = await POST_AUTH_LOGIN_SESSION_KEY();
      const { data: resData } = sessionKey.data;

      const encData = encryptWithBase64DerPublicKey(
        JSON.stringify({
          otp: enteredOtp,
        }),
        resData.publicKey
      );
      const response = await VERIFY_OTP({
        session_id: resData.sessionId,
        encrypted_payload: encData,
      });
      const { data } = response.data;
      setVerifyOtpResponse({
        data: data,
        status: "resolved",
      });
      await onAuthenticationResponse(data);
    } catch (err: unknown) {
      const error = err as Error & {
        response?: { data?: { message?: string } };
      };
      let message = t(`failedVerifyOtp`);
      if (error?.response?.data?.message?.includes("invalid otp")) {
        message = t(`invalidOtp`);
      }
      toast.error(message);
      setVerifyOtpResponse({
        data: null,
        status: "rejected",
      });
    } finally {
      if (toastId) toast.dismiss(toastId);
    }
  };

  const onAuthenticationResponse = async (data: any) => {
    const imgBlobUrl: string | null = await urlToBase64(
      data?.imageUrl || ""
    ).catch(() => null);
    const banBlobUrl: string | null = await urlToBase64(
      data?.bannerUrl || ""
    ).catch(() => null);

    const user = {
      role: data.role,
      emailVerified: data.emailVerified,
      imageUrl: imgBlobUrl,
      bannerUrl: banBlobUrl ? banBlobUrl : null,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      enable2FA: data.enable2FA || false,
      organization: data.organization,
      did: data.did || [],
      accountId: data.accountId,
      userName: data.userName || "",
      phoneNumber: data.phoneNumber || "",
      phoneCountryCode: data?.phoneCountryCode || "",
      phoneCode: data?.phoneCode || "",
      passwordResetRequired: data?.passwordResetRequired || false,
      passwordResetSkippable: data?.passwordResetSkippable || false,
      asymmetricPubKey: data?.asymmetricPubKey,
    };

    handleUpdateUser(user);

    if (data.role === ROLES.Admin) {
      let isAsymmetricPubKey =
        data?.asymmetricPubKey ||
        (await userKeypairs.generateAysmetricKeypair()).keys.publicKey;

      if (isAsymmetricPubKey && (!data?.did?.length || !data?.did[0]?.did)) {
        await userKeypairs.generateDid({
          callBackOnSuccess: ({ did }) => {
            handleUpdateUser({ did });
          },
        });
      }
    } else if (data.role === ROLES.SuperAdmin && !data?.asymmetricPubKey) {
      await userKeypairs.generateAysmetricKeypair();
    }
    navigate(data.role.toLowerCase());
  };

  const handleClearUser = useCallback(() => {
    dispatch(clearUser());
    navigate("/login", { replace: true });
  }, [dispatch, navigate]);

  const logout = useCallback(
    async (type: string) => {
      try {
        await AmplifyAuth.signOut();
        await POST_AUTH_LOGOUT({ type });
        handleClearUser();
      } catch (err) {
        toast.error(t("toast.FailedLogout"));
      }
    },
    [handleClearUser, t]
  );

  const logout_reserPasswordAfter60Day = async () => {
    await AmplifyAuth.signOut();
    await POST_AUTH_LOGOUT({ type: "LOGIN" });
    dispatch(clearUser());
  };

  const contextValue: AuthContextType = {
    setUser: handleUpdateUser,
    user,
    login,
    logout,
    loginResponse,
    verifyOtp,
    logout_reserPasswordAfter60Day,
  };

  useEffect(() => {
    injectLogout(handleClearUser);
  }, [handleClearUser]);

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
